import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const ProjectManagement = () => {
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tech_stack: '',
    status: 'completed',
    category: '',
    image_url: '',
    project_url: '',
    featured: false,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tech_stack: formData.tech_stack.split(',').map(t => t.trim()),
      };

      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, data);
        toast.success('Project updated successfully');
      } else {
        await api.post('/projects', data);
        toast.success('Project created successfully');
      }

      setShowModal(false);
      setEditingProject(null);
      setFormData({ name: '', description: '', tech_stack: '', status: 'completed', category: '', image_url: '', project_url: '', featured: false });
      fetchProjects();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description,
      tech_stack: project.tech_stack.join(', '),
      status: project.status,
      category: project.category,
      image_url: project.image_url || '',
      project_url: project.project_url || '',
      featured: project.featured,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await api.delete(`/projects/${id}`);
        toast.success('Project deleted');
        fetchProjects();
      } catch (error) {
        toast.error('Failed to delete project');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin"><ArrowLeft className="w-5 h-5" /></Link>
              <h1 className="text-2xl font-heading font-bold">Project Management</h1>
            </div>
            <Button onClick={() => setShowModal(true)} className="bg-primary" data-testid="create-project-button">
              <Plus className="w-4 h-4 mr-2" />New Project
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div key={project.id} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`project-card-${project.id}`}>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{project.name}</h3>
              <p className="text-slate-600 text-sm mb-3">{project.description}</p>
              <div className="flex space-x-2 mb-3">
                {project.tech_stack.slice(0, 3).map((tech, i) => (
                  <span key={i} className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs">{tech}</span>
                ))}
              </div>
              <div className="flex space-x-2">
                <button onClick={() => handleEdit(project)} className="text-blue-600"><Edit className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(project.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingProject ? 'Edit Project' : 'New Project'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
            <div><Label>Description *</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required /></div>
            <div><Label>Tech Stack (comma-separated) *</Label><Input value={formData.tech_stack} onChange={(e) => setFormData({...formData, tech_stack: e.target.value})} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Status *</Label><Input value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} required /></div>
              <div><Label>Category *</Label><Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required /></div>
            </div>
            <div><Label>Image URL</Label><Input type="url" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} /></div>
            <div><Label>Project URL</Label><Input type="url" value={formData.project_url} onChange={(e) => setFormData({...formData, project_url: e.target.value})} /></div>
            <div className="flex items-center space-x-2"><Switch checked={formData.featured} onCheckedChange={(checked) => setFormData({...formData, featured: checked})} /><Label>Featured</Label></div>
            <Button type="submit" className="w-full bg-primary">{editingProject ? 'Update' : 'Create'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectManagement;
